import { isSignedInToAnotherAccount } from 'src/utils/platform-error-message';

// What a marketplace's claim answers once it has been sent the most new listings Voolist sends it in a day,
// or while Voolist has paused new listings there (createHeldBack in src/utils/extension-publish-rules.js).
const HOLDS_FOR_THE_DAY = Object.freeze(['capReached', 'marketplaceOff']);

/**
 * The marketplaces a run stops sending to, and what it answers their items with instead.
 *
 * Some answers hold for every item left for a marketplace: nothing about the next listing changes them, so
 * sending it would only spend another trip through the extension to hear the same refusal. Once one item
 * gets such an answer, the run answers the rest of that marketplace's items with the same sentence, without
 * sending them, and carries on with the other marketplaces:
 *  - Chrome is signed in to another account than the one the seller connected. The extension sends nothing
 *    to Depop then, nor to Poshmark while Chrome is signed in to another closet on the account's site;
 *  - the marketplace was sent the most new listings Voolist sends it in a day, or Voolist has paused new
 *    listings there (Vestiaire). Nothing was sent, and each item's create stays queued.
 *
 * Kept per marketplace — every Poshmark item of a run goes to the one Poshmark site the extension has
 * recorded, so a Poshmark refusal is about that site — and per run: the seller can switch the login, or
 * wait, and start again.
 */
export function createMarketplaceStops() {
  const stops = new Map();

  return {
    /**
     * The answer this run gives the marketplace's items from now on, or null to send them.
     * @param {string} platform
     * @returns {Object|null} the stopping answer, a copy per item
     */
    answerFor: (platform) => (stops.has(platform) ? { ...stops.get(platform) } : null),

    /**
     * Note what one item was answered. An answer that holds for every item left stops the marketplace for
     * the rest of the run, answered with the same sentence.
     * @param {string} platform
     * @param {Object} result - What the publish answered
     */
    record: (platform, result) => {
      if (!platform || stops.has(platform)) return;
      if (isSignedInToAnotherAccount(result?.error)) {
        stops.set(platform, { success: false, error: result.error, platform });
      } else if (HOLDS_FOR_THE_DAY.includes(result?.heldBack)) {
        stops.set(platform, { success: false, error: result.error, platform, heldBack: result.heldBack, retryAfter: result.retryAfter ?? null });
      }
    },
  };
}
